// src/videoConfig.ts

import { ACTION_TYPES, PANEL_VARIANTS } from './constants';

/**
 * Payload pro různé typy akcí
 */
export interface LinkActionPayload {
  url: string;
  timeToShow: number;
}

export interface PanelActionPayload {
  variant: typeof PANEL_VARIANTS[keyof typeof PANEL_VARIANTS];
  timeToShow: number;
}

export interface PlanActionPayload {
  url_gold_cz: string;
  url_gold_sk: string;
  url_rentier_cz: string;
  url_rentier_sk: string;
  url_max_cz: string;
  url_max_sk: string;
  timeToShow: number;
}

/**
 * Základní akce bez payloadu
 */
export interface BaseAction {
  type: typeof ACTION_TYPES.INTRO
    | typeof ACTION_TYPES.SELECT_AVATAR
    | typeof ACTION_TYPES.SELECTED_AVATAR
    | typeof ACTION_TYPES.SWITCH_INTRO
    | typeof ACTION_TYPES.SWITCH
    | typeof ACTION_TYPES.CONTACT_FORM
    | typeof ACTION_TYPES.END
    | typeof ACTION_TYPES.XROAD
    | typeof ACTION_TYPES.INGOLD_PAY;
  payload?: Record<string, never>;
}

/**
 * Akce s link payloadem
 */
export interface LinkAction {
  type: typeof ACTION_TYPES.LINK;
  payload: LinkActionPayload;
}

/**
 * Akce s panel payloadem
 */
export interface PanelAction {
  type: typeof ACTION_TYPES.PANEL;
  payload: PanelActionPayload;
}

/**
 * Akce pro varianty účtů (PLAN_INTRO, EXAMPLES, FEES)
 */
export interface PlanVariantAction {
  type: typeof ACTION_TYPES.PLAN_INTRO
    | typeof ACTION_TYPES.EXAMPLES
    | typeof ACTION_TYPES.FEES;
  src_gold_r_cz: string | null;
  src_gold_r_sk: string | null;
  src_rentier_r_cz: string | null;
  src_rentier_r_sk: string | null;
  src_max_r_cz: string | null;
  src_max_r_sk: string | null;
  src_gold_s_cz: string | null;
  src_gold_s_sk: string | null;
  src_rentier_s_cz: string | null;
  src_rentier_s_sk: string | null;
  src_max_s_cz: string | null;
  src_max_s_sk: string | null;
  payload: PlanActionPayload;
}

/**
 * Všechny možné akce
 */
export type VideoAction =
  | BaseAction
  | LinkAction
  | PanelAction
  | PlanVariantAction;

/**
 * Type guard pro PlanVariantAction
 */
export function isPlanVariantAction(action: VideoAction | null | undefined): action is PlanVariantAction {
  if (!action) return false;
  return action.type === ACTION_TYPES.PLAN_INTRO ||
    action.type === ACTION_TYPES.EXAMPLES ||
    action.type === ACTION_TYPES.FEES;
}

/**
 * Type guard pro LinkAction
 */
export function isLinkAction(action: VideoAction | null | undefined): action is LinkAction {
  if (!action) return false;
  return action.type === ACTION_TYPES.LINK;
}

/**
 * Type guard pro PanelAction
 */
export function isPanelAction(action: VideoAction | null | undefined): action is PanelAction {
  if (!action) return false;
  return action.type === ACTION_TYPES.PANEL;
}

/**
 * Struktura jednoho videa v konfiguraci
 */
export interface VideoConfig {
  titleKey: string | null;
  src_cz: string | null;
  src_sk: string | null;
  src_r_cz: string | null;
  src_r_sk: string | null;
  src_s_cz: string | null;
  src_s_sk: string | null;
  isCategoryVideo: boolean;
  hasButton: boolean;
  action: VideoAction | null;
  tag: string | null;
}

/**
 * Parametry pro vytvoření video objektu
 */
interface CreateVideoParams {
  titleKey?: string | null;
  src_cz?: string | null;
  src_sk?: string | null;
  src_r_cz?: string | null;
  src_r_sk?: string | null;
  src_s_cz?: string | null;
  src_s_sk?: string | null;
  isCategoryVideo?: boolean;
  action?: VideoAction | null;
  tag?: string | null;
}

/**
 * Pomocná funkce pro vytvoření objektu videa.
 */
function createVideo({
  titleKey = null,
  src_cz = null,
  src_sk = null,
  src_r_cz = null,
  src_r_sk = null,
  src_s_cz = null,
  src_s_sk = null,
  isCategoryVideo = false,
  action = null,
  tag = null
}: CreateVideoParams): VideoConfig {
  return { titleKey, src_cz, src_sk, src_r_cz, src_r_sk, src_s_cz, src_s_sk, isCategoryVideo, hasButton: false, action, tag };
}

export const videos: readonly VideoConfig[] = [

  createVideo({
    titleKey: 'crossroad', src_cz: null, src_sk: null, isCategoryVideo: true, action: { type: ACTION_TYPES.XROAD }
  }),

  // Úvod a výběr avatara
  createVideo({
    titleKey: 'intro',
    src_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/00%20Intro%20.mp4',
    src_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/00%20Intro.mp4',
    isCategoryVideo: true, action: { type: ACTION_TYPES.INTRO, payload: {} }
  }),
  createVideo({
    titleKey: 'selectAvatar',
    src_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/00%20Vyber.mp4',
    src_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/00%20Vyber-2.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.SELECT_AVATAR, payload: {} }
  }),
  createVideo({
    titleKey: 'avatarIntro',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/00%20Vyber%20Robert.mp4', // šum v pozadí
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/00%20Vyber%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/00%20Vyber%20Silvie.mp4', //šum v pozadí
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/00%20Vyber%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.SELECTED_AVATAR, payload: {} }
  }),

  // Krok 1 - Nástin situace
  createVideo({
    titleKey: 'situationOutline',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.%20Nastineni%20Situace.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.0.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.%20Nastineni%20Situace.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.0.mp4',
    isCategoryVideo: true
  }),
  createVideo({
    titleKey: 'sickWorld',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.1.%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.1. Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.1.%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.1.%20Silvie.mp4',
    isCategoryVideo: false
  }),
  createVideo({
    titleKey: 'goldStandard',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.2.%20Zlaty%20Standart_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.2Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.2%20Zlaty%20Standart_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.2%20Silvie.mp4',
    isCategoryVideo: false
  }),
  createVideo({
    titleKey: 'bricsPlus',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.3.%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.3.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.3.%20Silvie_1.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.3.%20Silvie.mp4',
    isCategoryVideo: false
  }),
  createVideo({
    titleKey: 'inflation',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.4.%20Inflace%20a%20jeji%20rust%20v%20EU_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.4.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.4.%20Inflace%20a%20jeji%20rust%20v%20EU_.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.4.%20Silvie.mp4',
    isCategoryVideo: false
  }),
  createVideo({
    titleKey: 'dedolarization',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.5.%20Proces%20dedolarizace_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.5.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/1.5.%20Proces%20dedolarizace_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/1.5.%20Silvie.mp4',
    isCategoryVideo: false
  }),

  // Krok 2 - Zlato jako řešení
  createVideo({
    titleKey: 'goldSolution',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.%20Zlato%20jako%20reseni.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.0.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.%20Zlato%20jako%20reseni_silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.0.%20Silvie.mp4',
    isCategoryVideo: true
  }),
  createVideo({
    titleKey: 'goldValueKeeper',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.1.%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.1.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.1.%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.1.%20Silvie.mp4',
    isCategoryVideo: false
  }),
  createVideo({
    titleKey: 'goldStock',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.2.%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.2.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.2.%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.2.%20Silvie.mp4',
    isCategoryVideo: false
  }),
  createVideo({
    titleKey: 'goldMining',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.3.%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.3.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/2.3.%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/2.3.%20Silvie.mp4',
    isCategoryVideo: false
  }),

  // Krok 3 - Představení společnosti
  createVideo({
    titleKey: 'companyPresentation',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.%20Predstaveni%20Spolecnosti.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.0.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.%20Predstaveni%20Spolecnosti_silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.0.%20Silvie.mp4',
    isCategoryVideo: true
  }),
  createVideo({
    titleKey: 'firegoldIbis',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.1.%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.1.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.1.%20Silvie-2.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.1.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.LINK, payload: { url: 'https://firegold.ibisingold.com/Shop/gold?PID=104242', timeToShow: 0 } }
  }),
  createVideo({
    titleKey: 'pamp',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.2.%20PAMP_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.2.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.2.%20%20PAMP_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.2.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.PANEL, payload: { variant: PANEL_VARIANTS.PAMP, timeToShow: 0 } }
  }),
  createVideo({
    titleKey: 'royalMint',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.3.%20The%20Royal%20Mint_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.3.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/3.3.%20%20The%20Royal%20Mint_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/3.3.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.PANEL, payload: { variant: PANEL_VARIANTS.ROYAL_MINT, timeToShow: 0 } }
  }),

  // Krok 4 - Představení Zlatých účtů
  createVideo({
    titleKey: 'accountsIntroCategory',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.%20Prestaveni%20Zlatych%20Uctu_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.0.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.%20Prestaveni%20Zlatych%20Uctu_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.0.%20Silvie.mp4',
    isCategoryVideo: true
  }),

  createVideo({
    titleKey: 'accountsIntroVideo',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.1.%20%20Predstaveni%20zlatych%20uctu_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.1.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.1.%20%20Predstaveni%20zlatych%20uctu_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.1.%20Silvie.mp4',
    isCategoryVideo: false,
    tag: "introAccounts"
  }),

  createVideo({
    titleKey: 'accountsIntroSwitch', src_cz: null, src_sk: null, isCategoryVideo: true, action: { type: ACTION_TYPES.SWITCH_INTRO }
  }),

  createVideo({
    titleKey: 'accountPresentation',
    isCategoryVideo: false,
    action: {
      type: ACTION_TYPES.PLAN_INTRO,
      src_gold_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.2.%20iiPlan%20Gold_Robert.mp4',
      src_gold_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.2.%20Robert.mp4',
      src_rentier_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.3.%20iiPlan%20Rentier_Robert.mp4',
      src_rentier_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.3.%20Robert.mp4',
      src_max_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.4.%20iiPlan%20Max_Robert.mp4',
      src_max_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.4.%20Robert.mp4',

      src_gold_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.2.%20iiPlan%20Gold_Silvie.mp4',
      src_gold_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.2.%20Silvie.mp4',
      src_rentier_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.3.%20iiPlan%20Rentier_Silvie.mp4',
      src_rentier_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.3.%20Silvie.mp4',
      src_max_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.4.%20iiPlan%20Max_Silvie.mp4',
      src_max_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.4.%20Silvie.mp4',

      payload: {
        url_gold_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_GOLD_CZ.pdf',
        url_gold_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_GOLD_SK.pdf',
        url_rentier_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_RENTIER_CZ.pdf',
        url_rentier_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_RENTIER_SK.pdf',
        url_max_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_MAX_CZ.pdf',
        url_max_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_MAX_SK.pdf',
        timeToShow: 0
      },
    }
  }),

  createVideo({
    titleKey: 'accountCosts',
    isCategoryVideo: false,
    action: {
      type: ACTION_TYPES.FEES,
      src_gold_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.5.1.%20Robert_1-2.mp4',
      src_gold_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.5%20Robert.mp4',
      src_rentier_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.5.2.%20Poplatky%20Rentier_Robert.mp4',
      src_rentier_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.5.2.%20Robert.mp4',
      src_max_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.5.3.%20Poplatky%20Max_Robert.mp4',
      src_max_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.5.3.%20Robert.mp4',
      
      src_gold_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.5.1.%20Silvie-2.mp4',
      src_gold_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.5%20Silvie.mp4',
      src_rentier_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.5.2.%20Poplatky%20Rentier_Silvie.mp4',
      src_rentier_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.5.2.%20Silvie.mp4',
      src_max_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.5.3.%20Poplatky%20Max_Silvie.mp4',
      src_max_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.5.3.%20Silvie.mp4',
      payload: {
        url_gold_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_GOLD_CZ.pdf',
        url_gold_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_GOLD_SK.pdf',
        url_rentier_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_RENTIER_CZ.pdf',
        url_rentier_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_RENTIER_SK.pdf',
        url_max_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_MAX_CZ.pdf',
        url_max_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_MAX_SK.pdf',
        timeToShow: 0
      },
    }
  }),

  createVideo({
    titleKey: 'accountExamples',
    isCategoryVideo: false,
    action: {
      type: ACTION_TYPES.EXAMPLES,
      src_gold_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.6.1.%20Priklady%20z%20praxe_GOLD_Robert.mp4',
      src_gold_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.6 Robert.mp4',
      src_rentier_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.6.2%20Priklady%20z%20praxe_RENTIER_Robert.mp4',
      src_rentier_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.6.2 Robert.mp4',
      src_max_r_cz: null,
      src_max_r_sk: null,

      src_gold_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.6.1.%20Priklady%20z%20praxe_GOLD_Silvie.mp4',
      src_gold_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.6%20Silvie.mp4',
      src_rentier_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/4.6.2%20Priklady%20z%20praxe_RENTIER_Silvie.mp4',
      src_rentier_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/4.6.2%20Silvie.mp4',
      src_max_s_cz: null,
      src_max_s_sk: null,

      payload: {
        url_gold_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_GOLD_CZ.pdf',
        url_gold_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_GOLD_SK.pdf',
        url_rentier_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_RENTIER_CZ.pdf',
        url_rentier_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_RENTIER_SK.pdf',
        url_max_cz: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_MAX_CZ.pdf',
        url_max_sk: 'https://isprodukcefiregold.blob.core.windows.net/files/iiplan_MAX_SK.pdf',
        timeToShow: 0
      },
    }
  }),


  // Krok 5 - Ingold Pay
  createVideo({
    titleKey: 'ingoldPayCategory',
    action: {
      type: ACTION_TYPES.INGOLD_PAY
    },
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.%20Ingold%20Pay_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.0.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.%20Ingold%20Pay_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.0.%20Silvie.mp4',
    isCategoryVideo: true
  }),

  createVideo({
    titleKey: 'ingoldPayPayments',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.1.%20%20Platebni%20system%20inGoldPay_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.1.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.1.%20%20Platebni%20system%20inGoldPay_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.1.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.LINK, payload: { url: 'https://ingoldpay.com/?PID=104242', timeToShow: 0 } }
  }),

  createVideo({
    titleKey: 'ingoldPayIndividuals',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.2.%20%20inGoldPay%20pro%20jednotlivce_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.2.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.2.%20%20inGoldPay%20pro%20jednotlivce_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.2.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.LINK, payload: { url: 'https://ingoldpay.com/?PID=104242', timeToShow: 0 } }
  }),

  createVideo({
    titleKey: 'ingoldPayCompanies',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.3.%20%20inGoldPay%20pro%20firmy_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.3.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/5.3.%20%20inGoldPay%20pro%20firmy_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/5.3.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.LINK, payload: { url: 'https://ingoldpay.com/?PID=104242', timeToShow: 0 } }
  }),

  // Krok 6 - Zabezpečení
  createVideo({
    titleKey: 'securityInsurance',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.%20Zabezpeceni_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.0.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.%20Zabezpeceni_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.0.%20Silvie.mp4',
    isCategoryVideo: true
  }),

  createVideo({
    titleKey: 'insurance',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.1.%20%20Robert_1.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.1.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.1.%20%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.1.%20Silvie-2.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.PANEL, payload: { variant: PANEL_VARIANTS.LLOYDS_INSURANCE, timeToShow: 0 } }
  }),

  createVideo({
    titleKey: 'supplierControl',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.2.%20%20Robert_1.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.2.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.2.%20%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.2.%20Silvie.mp4',
    isCategoryVideo: false
  }),

  createVideo({
    titleKey: 'auditorControl',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.3.%20%20Robert_1.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.3.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.3.%20%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.3.%20Silvie.mp4',
    isCategoryVideo: false, action: { type: ACTION_TYPES.LINK, payload: { url: 'https://isprodukcefiregold.blob.core.windows.net/files/Zpra%CC%81va_auditora_2023.pdf', timeToShow: 0 } }
  }),

  createVideo({
    titleKey: 'ownership',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.4.%20%20Vlastnictvi_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.4.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.4.%20%20Vlastnictvi_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.4.%20Silvie.mp4',
    isCategoryVideo: false
  }),

  createVideo({
    titleKey: 'expansion',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.5.%20%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.5.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/6.5.%20%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/6.5.%20Silvie.mp4',
    isCategoryVideo: false
  }),

  // Krok 7 - Otevření účtu
  createVideo({
    titleKey: 'openAccountCategory',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/7.%20Otevreni%20Uctu_Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/7.0.%20Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/7.%20Otevreni%20Uctu_Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/7.0.%20Silvie.mp4',
    isCategoryVideo: true
  }),


  // Přepínač mezi Zlatými účty / kontakt
  createVideo({ titleKey: 'openAccountSwitch', src_cz: null, src_sk: null, isCategoryVideo: true, action: { type: ACTION_TYPES.SWITCH },  tag: "openAccount" }),
  createVideo({ titleKey: 'openAccountForm', src_cz: null, src_sk: null, isCategoryVideo: true, action: { type: ACTION_TYPES.CONTACT_FORM } }),

  // KONEC
  createVideo({
    titleKey: 'finalWord',
    src_r_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/9.%20KONEC%20Robert.mp4',
    src_r_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/KONEC Robert.mp4',
    src_s_cz: 'https://isprodukcefiregold.blob.core.windows.net/videos/KONEC%20Silvie.mp4',
    src_s_sk: 'https://isprodukcefiregold.blob.core.windows.net/videos/SK/KONEC Silvie.mp4',
    isCategoryVideo: true, action: { type: ACTION_TYPES.END }
  })
] as const;
